import React, { useCallback, useState, useEffect } from 'react';
import styles from './tag-filter.module.scss';
import { TAGS_KEYS } from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import { useSelector } from 'react-redux';
import { Checkbox } from 'antd';
import { SingleTagIcon } from '../../tags/tagsIcons/SingleTagIcon.component';
import { Lang } from 'shared/constants/lang';
import _ from 'lodash';
import { FilterIcon } from '../../../images';
import { Label } from '../../core/label';
import { hasResults } from 'shared/utils/search.utils';
import { Button } from '../../design-system/buttons';

const MAX_PER_SEARCH = 3;
const FILTERS_INITIAL_STATE = {
  [TAGS_KEYS.SPECIALTY]: new Set(),
  [TAGS_KEYS.SUBSPECIALTY]: new Set(),
  [TAGS_KEYS.CONTENT_TYPE]: new Set(),
  [TAGS_KEYS.TARGET_AUDIENCE]: new Set(),
  LABELS: new Set(),
};

const RenderTags = ({
  title = '',
  tagKey,
  tags = {},
  hideTag = false,
  renderLabel,
  addFilter,
  filters,
}) => {
  const LANG_TAGS = useSelector(getLang('TAGS')) ?? Lang.TAGS;
  const LANG_FILTERS = useSelector(getLang('FILTERS')) ?? Lang.FILTERS;
  const keyFilters = Object.keys(tags);
  const [showMore, setShowMore] = useState(false);

  if (keyFilters.length === 0) return null;

  return (
    <div className={styles.tagContainer} key={tagKey}>
      <h4>{title}</h4>
      {keyFilters
        .sort((a, b) => tags[b] - tags[a])
        .filter((v, i) => (!showMore ? i < MAX_PER_SEARCH : true))
        .map(key => {
          return (
            <div className={styles.item} key={`${tagKey}-${key}`}>
              <div className={styles.checkbox}>
                <Checkbox
                  aria-label={key}
                  onChange={e => {
                    // Add custom properties to the event target
                    e.target.tag = key;
                    e.target.tagKey = tagKey;
                    addFilter(e);
                  }}
                  checked={filters[tagKey].has(key)}
                />
              </div>
              {!hideTag && <SingleTagIcon tag={key} darkBackground />}
              <span className={styles.tagKey}>
                {renderLabel ? renderLabel(key) : LANG_TAGS[key] || key}
              </span>
            </div>
          );
        })}
      {keyFilters.length > MAX_PER_SEARCH && (
        <Button
          type="link"
          className={styles.showButton}
          onClick={() => setShowMore(prev => !prev)}>
          {!showMore ? LANG_FILTERS.SHOW_MORE : LANG_FILTERS.SHOW_LESS}
        </Button>
      )}
    </div>
  );
};

const useTagFilters = ({ results, term, initialFilters = null }) => {
  const LANG = useSelector(getLang('TAGS_FORM')) ?? Lang.TAGS_FORM;
  const LANG_FILTERS = useSelector(getLang('FILTERS')) ?? Lang.FILTERS;

  // Use initialFilters if provided, otherwise use default
  const initialState = initialFilters || _.cloneDeep(FILTERS_INITIAL_STATE);
  const [filters, setFilters] = useState(initialState);

  // Track if this is the first mount to avoid clearing URL filters
  const isFirstMountRef = React.useRef(true);
  const prevTermRef = React.useRef(term);
  const hasInitialFiltersRef = React.useRef(!!initialFilters);

  useEffect(() => {
    // Don't clear filters on first mount (preserves URL filters)
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      prevTermRef.current = term;
      return;
    }

    // If we had initial filters from URL, don't clear on first term change
    // (This handles the case where term goes from '' to 'test' on mount)
    if (hasInitialFiltersRef.current && prevTermRef.current === '') {
      prevTermRef.current = term;
      return;
    }

    // Only clear filters if term actually changed
    if (term !== prevTermRef.current) {
      setFilters(_.cloneDeep(FILTERS_INITIAL_STATE));
      prevTermRef.current = term;
    }
  }, [term]);

  const clearFilters = useCallback(() => {
    setFilters(_.cloneDeep(FILTERS_INITIAL_STATE));
  }, [setFilters]);

  const labelsItems = useSelector(state => state.labels.list) ?? {};

  const addFilter = useCallback(
    e => {
      const { tag, tagKey, checked } = e.target;

      if (checked) {
        setFilters(prevFilters => ({
          ...prevFilters,
          [tagKey]: new Set([...prevFilters[tagKey], tag]),
        }));
      } else {
        setFilters(prevFilters => {
          const next = new Set(prevFilters[tagKey]);
          next.delete(tag);
          return { ...prevFilters, [tagKey]: next };
        });
      }
    },
    [setFilters],
  );

  // ignore EVERYONE tag in filters
  if (results?.tagsMap?.targetAudience?.EVERYONE) {
    delete results.tagsMap.targetAudience.EVERYONE;
  }

  const FilterComponent = (
    <div>
      <div className={styles.header}>
        <div className={styles.container}>
          <img className={styles.icon} src={FilterIcon} alt={LANG_FILTERS.TITLE} />
          <h2 className={styles.title}>{LANG_FILTERS.TITLE}</h2>
        </div>
        <Button type="link" className={styles.clearButton} onClick={clearFilters}>
          {LANG_FILTERS.CLEAR}
        </Button>
      </div>

      <RenderTags
        key="LABELS"
        addFilter={addFilter}
        filters={filters}
        title={LANG.LABELS}
        tagKey="LABELS"
        tags={results?.tagsMap?.labels}
        hideTag={true}
        renderLabel={key => (
          <div className={styles.labelOption}>
            <Label key={key} color={labelsItems[key]?.color} value={key} />
            <p className={styles.labelName}>{labelsItems[key]?.name}</p>
          </div>
        )}
      />

      <RenderTags
        key={TAGS_KEYS.SPECIALTY}
        addFilter={addFilter}
        filters={filters}
        title={LANG.SPECIALTY_PLACEHOLDER}
        tagKey={TAGS_KEYS.SPECIALTY}
        tags={results?.tagsMap?.specialty}
      />

      <RenderTags
        key={TAGS_KEYS.SUBSPECIALTY}
        addFilter={addFilter}
        filters={filters}
        title={LANG.SUBSPECIALTY_PLACEHOLDER}
        tagKey={TAGS_KEYS.SUBSPECIALTY}
        tags={results?.tagsMap?.subspecialty}
      />

      <RenderTags
        key={TAGS_KEYS.CONTENT_TYPE}
        addFilter={addFilter}
        filters={filters}
        title={LANG.CONTENT_TYPE_PLACEHOLDER}
        tagKey={TAGS_KEYS.CONTENT_TYPE}
        tags={results?.tagsMap?.contentType}
      />

      <RenderTags
        key={TAGS_KEYS.TARGET_AUDIENCE}
        addFilter={addFilter}
        filters={filters}
        title={LANG.TARGET_AUDIENCE_PLACEHOLDER}
        tagKey={TAGS_KEYS.TARGET_AUDIENCE}
        tags={results?.tagsMap?.targetAudience}
        hideTag
      />
    </div>
  );

  return {
    FilterComponent,
    filters,
    filtersExist: hasResults(results?.tagsMap),
  };
};

export { useTagFilters };
